import { useEffect, useMemo, useState } from 'react';
// @mui
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
// components
import Iconify from '../../../components/Iconify';
// utils
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';

// ----------------------------------------------------------------------
// Diploma-only edit for the customer-care role.
//
// customer-care may look at a student and change exactly one thing: which
// diploma(s) they are enrolled in. It deliberately does NOT reuse UserModal —
// that form posts the whole user (package, subscription, segment fields), and
// the segment endpoint behind it can grant free lifetime premium. This dialog
// talks only to PATCH /users/:id/diplomas.
// ----------------------------------------------------------------------

type Enrollment = { diploma: string; batchName: string };

type Props = {
  open: boolean;
  user: any | null;
  onClose: (saved?: boolean) => void;
};

// Read the student's current enrolments, tolerating both the multi-diploma
// array and the legacy single `diploma` + `batchName` pair.
function readEnrollments(user: any): Enrollment[] {
  if (!user) return [];
  const arr = user.diplomas;
  if (Array.isArray(arr) && arr.length) {
    return arr
      .map((e: any) => ({
        diploma: String(typeof e?.diploma === 'object' ? e?.diploma?._id : e?.diploma || ''),
        batchName: String(e?.batchName || ''),
      }))
      .filter((e: Enrollment) => !!e.diploma);
  }
  const legacy = typeof user.diploma === 'object' ? user.diploma?._id : user.diploma;
  return legacy ? [{ diploma: String(legacy), batchName: String(user.batchName || '') }] : [];
}

export default function AssignDiplomasDialog({ open, user, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [diplomas, setDiplomas] = useState<any[]>([]);
  const [rows, setRows] = useState<Enrollment[]>([]);
  const [saving, setSaving] = useState(false);

  // Re-seed the form every time the dialog opens on a (possibly different)
  // student — otherwise it would keep showing the previous student's rows.
  useEffect(() => {
    if (open) setRows(readEnrollments(user));
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    axiosInstance
      .get(APIS.DIPLOMAS.ACTIVE)
      .then((res) => {
        const d: any = res.data?.data ?? res.data;
        setDiplomas(Array.isArray(d) ? d : []);
      })
      .catch(() => setDiplomas([]));
  }, [open]);

  const batchesOf = (diplomaId: string): any[] => {
    const d = diplomas.find((x: any) => x._id === diplomaId);
    return (d?.batches || []).filter((b: any) => b.active !== false);
  };

  // A diploma with no batch selected is incomplete; a duplicate diploma would
  // silently collapse on save. Block both rather than saving something the
  // student sees differently.
  const error = useMemo(() => {
    if (rows.some((r) => !r.diploma)) return 'Every row needs a diploma selected.';
    const ids = rows.map((r) => r.diploma);
    if (new Set(ids).size !== ids.length) return 'The same diploma is listed twice.';
    return '';
  }, [rows]);

  const setRow = (index: number, patch: Partial<Enrollment>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const handleSave = async () => {
    if (!user?._id || error) return;
    setSaving(true);
    try {
      await axiosInstance.patch(APIS.USERS.DIPLOMAS(user._id), {
        diplomas: rows.map((r) => ({ diploma: r.diploma, batchName: r.batchName || '' })),
      });
      enqueueSnackbar('Diplomas updated');
      onClose(true);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Unable to update diplomas', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>Diplomas — {user?.name || user?.email || 'student'}</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Assign or remove this student&apos;s diplomas. Nothing else on the account changes.
        </Typography>

        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No diploma assigned.
          </Typography>
        )}

        <Stack spacing={1.5}>
          {rows.map((row, index) => (
            <Box
              key={index}
              sx={{ p: 1.5, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  select
                  size="small"
                  label="Diploma"
                  sx={{ flex: 1 }}
                  value={row.diploma}
                  // Changing the diploma invalidates the batch chosen under the
                  // previous one, so clear it in the same update.
                  onChange={(e) => setRow(index, { diploma: e.target.value, batchName: '' })}
                >
                  <MenuItem value="">Select diploma</MenuItem>
                  {diplomas.map((d: any) => (
                    <MenuItem key={d._id} value={d._id}>
                      {d.title}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Batch"
                  sx={{ flex: 1 }}
                  disabled={!row.diploma}
                  value={row.batchName}
                  onChange={(e) => setRow(index, { batchName: e.target.value })}
                >
                  <MenuItem value="">Select batch</MenuItem>
                  {batchesOf(row.diploma).map((b: any) => (
                    <MenuItem key={b._id || b.name} value={b.name}>
                      {b.name}
                    </MenuItem>
                  ))}
                </TextField>

                <IconButton
                  color="error"
                  onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Iconify icon="eva:trash-2-outline" />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>

        <Button
          size="small"
          sx={{ mt: 2 }}
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => setRows((prev) => [...prev, { diploma: '', batchName: '' }])}
        >
          Add diploma
        </Button>

        {!!error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={() => onClose(false)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !!error}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
